import React, { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useGetWebsitesQuery, useGetWebsitesByTiersQuery } from "../../app/api/platform/platformWebsitesApiSlice";
import {
  useGetWebsitesAnalyticsIssuesQuery,
  useGetWebsitesAnalyticsIssueDiscardsQuery,
} from "../../app/api/platform/platformApiSlice";
import { PAYING_TIERS } from "../../constants/customers";
import {
  AGENT_GROUPINGS,
  applyListFilters,
  buildAgentStats,
  buildAgentTitleMap,
  buildClientStats,
  buildMatrix,
  buildTotals,
  buildWeeklyTrend,
  categorizeDiscards,
  describeVariants,
  filterDiscards,
  rankHeatmapAxes,
} from "./issueAnalyticsHelpers";
import AgentHeatmap from "./issueAnalytics/AgentHeatmap";
import DrillDown from "./issueAnalytics/DrillDown";
import KpiCards from "./issueAnalytics/KpiCards";
import RankList from "./issueAnalytics/RankList";
import TrendChart from "./issueAnalytics/TrendChart";
import WebsiteFilter from "./issueAnalytics/WebsiteFilter";
import { REASON_BY_ID } from "./issueAnalytics/discardReasons";
import { fmtPct, fmtRange, MIN_VOLUME_OPTIONS } from "./issueAnalytics/stats";
import { parseUrlState, serializeUrlState } from "./issueAnalytics/urlState";

// Issue Analytics: reject-rate view per agent × client over production issues.
//
// State lives in the URL (issueAnalytics/urlState.js) so a view can be shared. Data
// is fetched once for the superset of the paying set and the selected websites, as
// weekly rows, and every panel is derived locally (issueAnalyticsHelpers.js): the
// website filter, grouping, volume floor and selections never refetch; only the time
// window and the date clock do.

const PRESET_RANGES = [
  { id: "7", label: "Last 7 days" },
  { id: "30", label: "Last 30 days" },
  { id: "90", label: "Last 90 days" },
  { id: "all", label: "All time" },
  { id: "custom", label: "Custom" },
];

const DATE_BASES = [
  { id: "created", label: "Issues surfaced (created)", field: "created_at", short: "issue creation date" },
  { id: "updated", label: "Discards made (updated)", field: "updated_at", short: "discard date" },
];

const HEATMAP_TOP_N = 12;
const HUB_ENV = "prod";

function presetToRange(preset, customStart, customEnd) {
  if (preset === "all") return { start: null, end: null };
  if (preset === "custom") {
    return {
      start: customStart ? new Date(customStart).toISOString() : null,
      end: customEnd ? new Date(customEnd).toISOString() : null,
    };
  }
  const days = parseInt(preset, 10);
  const end = new Date();
  const start = new Date(end.getTime() - days * 24 * 60 * 60 * 1000);
  return { start: start.toISOString(), end: end.toISOString() };
}

const sameMembers = (a, b) => a.length === b.length && a.every((x) => b.includes(x));

function VariantList({ variants, selectedVariant, onSelect }) {
  return (
    <ul className='mt-1 space-y-0.5 border-l-2 border-border-subtle pl-2'>
      {variants.map((v) => {
        const isSel = selectedVariant === v.key;
        return (
          <li key={v.key}>
            <button
              type='button'
              onClick={() => onSelect(isSel ? "" : v.key)}
              title={v.title}
              className={`w-full text-left px-2 py-1 rounded-md flex items-center gap-2 text-caption ${
                isSel ? "bg-accent-purple/10 text-text-primary" : "hover:bg-surface-neutral text-text-secondary"
              }`}>
              <span className='flex-1 min-w-0 truncate'>{v.title}</span>
              <span className='shrink-0 text-text-tertiary'>
                {v.total.toLocaleString()} · {v.websiteCount} client{v.websiteCount === 1 ? "" : "s"}
              </span>
              <span className={`shrink-0 w-12 text-right font-semibold ${v.rejectRate == null ? "text-text-tertiary" : "text-text-primary"}`}>
                {fmtPct(v.rejectRate)}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function IssueAnalytics() {
  // ---- URL state ----
  const [searchParams, setSearchParams] = useSearchParams();
  const view = useMemo(() => parseUrlState(searchParams), [searchParams]);
  const updateView = useCallback(
    (patch) => {
      setSearchParams((prev) => serializeUrlState({ ...parseUrlState(prev), ...patch }), { replace: true });
    },
    [setSearchParams]
  );
  const { preset, start: customStart, end: customEnd, basis, group: agentGrouping, minVolume, agent: selectedAgent, variant: selectedVariant, client: selectedWebsite, reason: reasonFilter } = view;
  const dateBasis = DATE_BASES.find((b) => b.id === basis) || DATE_BASES[0];

  const { start: rangeStart, end: rangeEnd } = useMemo(
    () => presetToRange(preset, customStart, customEnd),
    [preset, customStart, customEnd]
  );

  // ---- Websites ----
  const { data: payingCustomers = [] } = useGetWebsitesByTiersQuery({ tiers: PAYING_TIERS });
  const payingWebsiteIds = useMemo(() => payingCustomers.map((c) => c.website_id), [payingCustomers]);
  const { data: websitesData = [], isLoading: isLoadingWebsites } = useGetWebsitesQuery({ useProd: true });
  const websiteById = useMemo(() => {
    const m = {};
    websitesData.forEach((w) => {
      m[w.website_id] = w;
    });
    return m;
  }, [websitesData]);

  const selectedWebsiteIdList = view.sites ?? payingWebsiteIds;
  const selectedWebsiteIds = useMemo(() => new Set(selectedWebsiteIdList), [selectedWebsiteIdList]);
  const setSelectedWebsites = (ids) => updateView({ sites: sameMembers(ids, payingWebsiteIds) ? null : ids });

  // Fetch the superset once; the website filter is applied locally.
  const fetchIds = useMemo(
    () => Array.from(new Set([...payingWebsiteIds, ...selectedWebsiteIdList])).sort(),
    [payingWebsiteIds, selectedWebsiteIdList]
  );
  const skip = fetchIds.length === 0;
  const baseArgs = { websiteIds: fetchIds, startDate: rangeStart, endDate: rangeEnd, dateField: dateBasis.field, useProd: true };

  const {
    data: cells = [],
    isLoading: isLoadingCells,
    isFetching: isFetchingCells,
    error: cellsError,
    refetch: refetchCells,
  } = useGetWebsitesAnalyticsIssuesQuery({ ...baseArgs, bucket: "week" }, { skip });
  const {
    data: discards = [],
    isLoading: isLoadingDiscards,
    isFetching: isFetchingDiscards,
    error: discardsError,
    refetch: refetchDiscards,
  } = useGetWebsitesAnalyticsIssueDiscardsQuery(baseArgs, { skip });

  const isLoading = isLoadingWebsites || isLoadingCells || isLoadingDiscards;
  const isUpdating = !isLoading && (isFetchingCells || isFetchingDiscards);
  const error = cellsError || discardsError;

  // ---- Local slices ----
  const scopedCells = useMemo(() => cells.filter((c) => selectedWebsiteIds.has(c.website_id)), [cells, selectedWebsiteIds]);
  const categorizedDiscards = useMemo(() => categorizeDiscards(discards), [discards]);
  const scopedDiscards = useMemo(
    () => categorizedDiscards.filter((d) => selectedWebsiteIds.has(d.website_id)),
    [categorizedDiscards, selectedWebsiteIds]
  );

  const agentTitleByKey = useMemo(
    () => buildAgentTitleMap(scopedCells, scopedDiscards, agentGrouping),
    [scopedCells, scopedDiscards, agentGrouping]
  );
  const labelForAgent = useCallback((key) => agentTitleByKey[key] || key, [agentTitleByKey]);

  const selection = useMemo(
    () => ({ agent: selectedAgent, variant: selectedVariant, client: selectedWebsite }),
    [selectedAgent, selectedVariant, selectedWebsite]
  );
  const totals = useMemo(() => buildTotals(scopedCells, selection, agentGrouping), [scopedCells, selection, agentGrouping]);

  const agentList = useMemo(
    () =>
      applyListFilters(buildAgentStats(scopedCells, { grouping: agentGrouping, selectedWebsite }), {
        minVolume,
        requireSignal: !selectedWebsite,
      }),
    [scopedCells, agentGrouping, selectedWebsite, minVolume]
  );
  const clientList = useMemo(
    () =>
      applyListFilters(
        buildClientStats(scopedCells, { grouping: agentGrouping, selectedAgent, selectedVariant, websiteById }),
        { minVolume, requireSignal: !selectedAgent }
      ),
    [scopedCells, agentGrouping, selectedAgent, selectedVariant, websiteById, minVolume]
  );

  const matrixData = useMemo(() => buildMatrix(scopedCells, agentGrouping), [scopedCells, agentGrouping]);
  const axes = useMemo(() => rankHeatmapAxes(matrixData, { minVolume, topN: HEATMAP_TOP_N }), [matrixData, minVolume]);

  const [discardSearch, setDiscardSearch] = useState("");
  const mixDiscards = useMemo(
    () => filterDiscards(scopedDiscards, { grouping: agentGrouping, ...selection }),
    [scopedDiscards, agentGrouping, selection]
  );
  const visibleDiscards = useMemo(
    () => filterDiscards(mixDiscards, { reason: reasonFilter, search: discardSearch }),
    [mixDiscards, reasonFilter, discardSearch]
  );

  const trend = useMemo(
    () => buildWeeklyTrend(scopedCells, { grouping: agentGrouping, ...selection }),
    [scopedCells, agentGrouping, selection]
  );

  // ---- Selection ----
  const selectAgent = (key) => updateView({ agent: key, variant: "" });
  const selectVariant = (agentKey, variantKey) => updateView({ agent: agentKey, variant: variantKey });
  const selectClient = (wid) => updateView({ client: wid });
  const clearSelection = () => updateView({ agent: "", variant: "", client: "", reason: "" });
  const hasSelection = Boolean(selectedAgent || selectedVariant || selectedWebsite || reasonFilter);

  const selectionLabel = hasSelection ? (
    <>
      Filtered to{" "}
      {selectedAgent && <span className='font-medium'>agent: {labelForAgent(selectedAgent)}</span>}
      {selectedVariant && <span className='font-medium'> › {selectedVariant}</span>}
      {selectedAgent && selectedWebsite && " · "}
      {selectedWebsite && (
        <span className='font-medium'>client: {(websiteById[selectedWebsite] && websiteById[selectedWebsite].website_name) || selectedWebsite}</span>
      )}
      {(selectedAgent || selectedWebsite) && reasonFilter && " · "}
      {reasonFilter && <span className='font-medium'>reason: {(REASON_BY_ID[reasonFilter] || {}).label || reasonFilter}</span>}
    </>
  ) : null;

  const exportDiscardsCsv = () => {
    const header = [
      "updated_at",
      "issue_id",
      "scan_id",
      "website_id",
      "agent_family_key",
      "agent_family_title",
      "agent_key",
      "agent_title",
      "reason_category",
      "severity",
      "issue_title",
      "discard_reason",
      "discarded_by",
    ];
    const rows = visibleDiscards.map((d) => header.map((k) => `"${String(d[k] ?? "").replace(/"/g, '""')}"`).join(","));
    const blob = new Blob([header.join(",") + "\n" + rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `issue_discards_${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const refresh = () => {
    refetchCells();
    refetchDiscards();
  };

  const selectClass =
    "px-3 py-2 text-body-xs bg-white border border-border-default rounded-xl text-text-secondary focus:outline-none focus:ring-2 focus:ring-accent-purple/20";

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-h2 text-text-primary'>Issue Analytics</h1>
        <p className='text-body-sm text-text-secondary mt-1'>
          Track client satisfaction and an ML reject-rate signal across paying production websites. See the caveat below —
          status updates aren't always recorded, so we use explicit discards as the primary signal.
        </p>
      </div>

      {/* Filters */}
      <div className='flex flex-wrap gap-3 items-end justify-between'>
        <div className='flex flex-wrap gap-3 items-end'>
          <div>
            <label htmlFor='ia-range' className='block text-body-xs font-medium text-text-secondary mb-1.5'>
              Time Range
            </label>
            <select id='ia-range' value={preset} onChange={(e) => updateView({ preset: e.target.value })} className={selectClass}>
              {PRESET_RANGES.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          {preset === "custom" && (
            <>
              <div>
                <label htmlFor='ia-start' className='block text-body-xs font-medium text-text-secondary mb-1.5'>
                  Start
                </label>
                <input id='ia-start' type='date' value={customStart} onChange={(e) => updateView({ start: e.target.value })} className={selectClass} />
              </div>
              <div>
                <label htmlFor='ia-end' className='block text-body-xs font-medium text-text-secondary mb-1.5'>
                  End
                </label>
                <input id='ia-end' type='date' value={customEnd} onChange={(e) => updateView({ end: e.target.value })} className={selectClass} />
              </div>
            </>
          )}

          <div>
            <label htmlFor='ia-basis' className='block text-body-xs font-medium text-text-secondary mb-1.5'>
              Window applies to
            </label>
            <select
              id='ia-basis'
              value={basis}
              onChange={(e) => updateView({ basis: e.target.value })}
              title='One clock for every number on the page: count issues by when they were surfaced, or by when they were last changed (for discards, when they were discarded).'
              className={selectClass}>
              {DATE_BASES.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor='ia-group' className='block text-body-xs font-medium text-text-secondary mb-1.5'>
              Group agents by
            </label>
            <select
              id='ia-group'
              value={agentGrouping}
              onChange={(e) => updateView({ group: e.target.value, agent: "", variant: "" })}
              title='Agent type folds every scan title of one agent (e.g. all "Product Info" pushes) into a single row; scan title keeps one row per title as typed.'
              className={selectClass}>
              {AGENT_GROUPINGS.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor='ia-min' className='block text-body-xs font-medium text-text-secondary mb-1.5'>
              Min. issues
            </label>
            <select
              id='ia-min'
              value={minVolume}
              onChange={(e) => updateView({ minVolume: parseInt(e.target.value, 10) })}
              title='Hide agents and clients with fewer issues than this from the ranked lists and the heatmap.'
              className={selectClass}>
              {MIN_VOLUME_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n === 0 ? "Any volume" : `${n}+`}
                </option>
              ))}
            </select>
          </div>

          <WebsiteFilter websites={websitesData} selectedIds={selectedWebsiteIds} payingIds={payingWebsiteIds} onChange={setSelectedWebsites} />
        </div>

        <div className='flex items-center gap-3'>
          {isUpdating && (
            <span className='text-body-xs text-text-tertiary flex items-center gap-2' role='status'>
              <span className='inline-block h-3 w-3 rounded-full border-2 border-accent-purple border-t-transparent animate-spin' />
              Updating…
            </span>
          )}
          <button
            type='button'
            onClick={refresh}
            className='flex items-center gap-2 px-4 py-2 text-body-xs font-semibold bg-button-primary text-white rounded-xl hover:bg-button-primary-hover transition-colors'>
            Refresh
          </button>
        </div>
      </div>

      {/* Loading / error / empty */}
      {isLoading && <div className='flex items-center justify-center py-12 text-text-secondary'>Loading issue analytics…</div>}
      {error && (
        <div className='bg-red-50 border border-red-200 rounded-lg p-6'>
          <span className='text-red-700'>Failed to load issue analytics.</span>
        </div>
      )}
      {!isLoading && !error && selectedWebsiteIds.size === 0 && (
        <div className='bg-surface-neutral-subtle border border-border-subtle rounded-2xl p-6 text-text-secondary'>
          Select at least one website to see analytics.
        </div>
      )}

      {!isLoading && !error && selectedWebsiteIds.size > 0 && (
        <div className={`space-y-6 transition-opacity ${isUpdating ? "opacity-60" : ""}`} aria-busy={isUpdating}>
          {/* Metric caveat */}
          <div className='bg-amber-50 border border-amber-200 rounded-xl p-3 text-body-xs text-amber-900'>
            <strong>Heads up:</strong> issue status isn't always updated by clients — some fix a finding without clicking
            "close", so an "open" issue isn't necessarily untriaged. We use <strong>reject rate = discarded / total</strong> as
            the primary signal because the denominator doesn't depend on close behavior. <strong>Confirmed-done rate</strong>{" "}
            only counts explicit closes. Rankings use the lower bound of a 95% interval, so a handful of issues cannot top the
            list. Every number on this page counts by {dateBasis.short}.
          </div>

          <KpiCards totals={totals} basisLabel={dateBasis.short} />

          <TrendChart
            trend={trend}
            basisLabel={dateBasis.short}
            subtitle={
              selectedAgent
                ? `${labelForAgent(selectedAgent)}${selectedVariant ? ` › ${selectedVariant}` : ""}${
                    selectedWebsite ? ` at ${(websiteById[selectedWebsite] && websiteById[selectedWebsite].website_name) || selectedWebsite}` : ""
                  }`
                : `Busiest agents${selectedWebsite ? ` at ${(websiteById[selectedWebsite] && websiteById[selectedWebsite].website_name) || selectedWebsite}` : ""} and all agents together`
            }
          />

          {/* Ranked lists */}
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
            <RankList
              title='Agents'
              subtitle={
                selectedWebsite
                  ? `Within client: ${(websiteById[selectedWebsite] && websiteById[selectedWebsite].website_name) || selectedWebsite}`
                  : "Bar shows closed (green) · discarded (red) · open (gray). Expand a row for its scan titles."
              }
              emptyMsg='No agents in this slice.'
              rows={agentList.rows}
              rowKey={(r) => r.key}
              rowName={(r) => r.title}
              rowSearchText={(r) => [r.title, ...r.variants.map((v) => v.title)].join(" ")}
              rowMeta={(r) => (
                <p className='text-caption text-text-tertiary truncate'>
                  {agentGrouping === "family" && r.variantCount > 1 ? `${r.variantCount} scan titles · ` : ""}
                  {r.websiteCount} client{r.websiteCount === 1 ? "" : "s"}
                </p>
              )}
              rowTooltip={(r) => (agentGrouping === "family" && r.variantCount > 1 ? `Scan titles in this group:\n${describeVariants(r.variants)}` : r.title)}
              primaryMetric={(r) => ({
                value: r.rejectRate,
                label: fmtPct(r.rejectRate),
                sub: r.rejectLow != null ? `${fmtRange(r.rejectLow, r.rejectHigh)} · ${r.total.toLocaleString()}` : `${r.total.toLocaleString()} total`,
              })}
              selected={selectedAgent}
              onSelect={(r) => selectAgent(selectedAgent === r.key ? "" : r.key)}
              canExpand={(r) => agentGrouping === "family" && r.variantCount > 1}
              renderExpanded={(r) => (
                <VariantList variants={r.variants} selectedVariant={selectedAgent === r.key ? selectedVariant : ""} onSelect={(v) => selectVariant(r.key, v)} />
              )}
              footnote={agentList.belowFloor > 0 ? `${agentList.belowFloor} agent${agentList.belowFloor === 1 ? "" : "s"} below the ${minVolume}-issue floor` : null}
            />
            <RankList
              title='Clients'
              subtitle={
                selectedAgent
                  ? `Within agent: ${labelForAgent(selectedAgent)}${selectedVariant ? ` › ${selectedVariant}` : ""}`
                  : "Bar shows closed (green) · discarded (red) · open (gray)."
              }
              emptyMsg='No clients in this slice.'
              rows={clientList.rows}
              rowKey={(r) => r.website_id}
              rowName={(r) => r.name}
              rowSearchText={(r) => `${r.name} ${r.website_id}`}
              rowMeta={(r) => (
                <p className='text-caption text-text-tertiary truncate'>
                  {r.agentCount} agent{r.agentCount === 1 ? "" : "s"}
                </p>
              )}
              primaryMetric={(r) => ({
                value: r.rejectRate,
                label: fmtPct(r.rejectRate),
                sub: r.rejectLow != null ? `${fmtRange(r.rejectLow, r.rejectHigh)} · ${r.total.toLocaleString()}` : `${r.total.toLocaleString()} total`,
              })}
              selected={selectedWebsite}
              onSelect={(r) => selectClient(selectedWebsite === r.website_id ? "" : r.website_id)}
              footnote={clientList.belowFloor > 0 ? `${clientList.belowFloor} client${clientList.belowFloor === 1 ? "" : "s"} below the ${minVolume}-issue floor` : null}
            />
          </div>

          <DrillDown
            selectionLabel={selectionLabel}
            onClearSelection={clearSelection}
            mixDiscards={mixDiscards}
            discards={visibleDiscards}
            reasonFilter={reasonFilter}
            onReasonFilter={(id) => updateView({ reason: id })}
            search={discardSearch}
            onSearch={setDiscardSearch}
            onExport={exportDiscardsCsv}
            websiteById={websiteById}
            labelForAgent={labelForAgent}
            grouping={agentGrouping}
            capped={discards.length >= 5000}
            hubEnv={HUB_ENV}
          />

          <AgentHeatmap
            agentKeys={axes.agentKeys}
            websiteKeys={axes.websiteKeys}
            matrix={matrixData.matrix}
            agentOverall={matrixData.agentOverall}
            clientOverall={matrixData.clientOverall}
            hiddenAgents={axes.hiddenAgents}
            hiddenClients={axes.hiddenClients}
            labelForAgent={labelForAgent}
            websiteById={websiteById}
            selectedAgent={selectedAgent}
            selectedWebsite={selectedWebsite}
            onSelectCell={(agentKey, wid, isSel) =>
              isSel ? updateView({ agent: "", variant: "", client: "" }) : updateView({ agent: agentKey, variant: "", client: wid })
            }
            topN={HEATMAP_TOP_N}
            minVolume={minVolume}
          />
        </div>
      )}
    </div>
  );
}

export default IssueAnalytics;
